import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Buttons, Inputs, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import {
  hideModalAction,
  setIsLoadingAction,
  setIsNotLoadingAction,
} from 'Store/modules/app/actions';
import useTranslation from 'Hooks/useTranslation';
import { useFetchSaveEfirmaMutation } from 'Containers/Account/components/AccountProfile/components/EFirmaForm/hooks';
import FormContainer from 'Components/UI/FormContainer';
import DocBox from 'Components/DocBox';
import { removeOverflow } from 'Styles/utils/utils';
import { routes } from 'Constants/routes';
import { EfirmaType } from 'Types/common';
import * as Styled from './styles';
import model from './model.json';

const initialState = {
  certificate: '',
  privateKey: '',
  passphrase: '',
};

const EfirmaPreviousYearModal = (): JSX.Element => {
  const [formValues, setFormValues] = useState<EfirmaType>(initialState);
  const [isValidPasswordFormat, setIsValidPasswordFormat] = useState(true);
  const dispatch = useDispatch();
  const clearLoading = (): unknown => dispatch(setIsNotLoadingAction());
  const { mutate, isSuccess } = useFetchSaveEfirmaMutation(clearLoading);
  const { t } = useTranslation(locizeNS.PY);
  const closeModal = (): void => {
    removeOverflow();
    dispatch(hideModalAction());
  };

  useEffect(() => {
    if (isSuccess) {
      closeModal();
      history.push(routes.PREVIOUS_YEARS_PROVIDER_ACCOUNT);
    }
  }, [isSuccess]);

  const handleChange = (value: string, id: string): void => {
    setFormValues(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleChangePassword = (value: string, id: string): void => {
    const isValid = /^.{8,256}$/.test(value);
    setIsValidPasswordFormat(isValid);
    handleChange(value, id);
  };

  const handleOnSubmit = (e: React.ChangeEvent): void => {
    e.preventDefault();
    mutate(formValues);
    dispatch(setIsLoadingAction());
  };

  return (
    <Styled.ModalContainer>
      <Styled.ModalContent direction="column">
        <Styled.CloseButton
          icon="IconCrossAccentM"
          onClick={closeModal}
          size="xxs"
          mode="secondary"
        />
        <FormContainer onSubmit={handleOnSubmit}>
          <Styled.EfirmaContainer direction="column" gap="dimension_08">
            <Text type="headline" color="dark" size="XS_L_S">
              {t('PreviousYear.modal.efirma.title')}
            </Text>
            <Text type="paragraphs" color="grey" size="XS_M_M">
              {t('PreviousYear.modal.efirma.subtitle')}
            </Text>
            {model.map(item => (
              <DocBox
                key={item.id}
                testID={item.testID}
                label={t(item.label)}
                setValue={handleChange}
                aceptFiles={item.aceptFiles}
                id={item.id}
                value={formValues[item.id]}
              />
            ))}
            <Inputs.Password
              type="password"
              testID="password"
              label={t('PreviousYear.inputLabel')}
              placeholder={t('PreviousYear.input.placeholder')}
              hasError={!isValidPasswordFormat}
              errorMsg={t('PreviousYear.Efirma.Error')}
              value={formValues.passphrase}
              onChange={val => handleChangePassword(val, 'passphrase')}
            />
            <Buttons.Classic
              size="l"
              type="submit"
              disabled={Object.values(formValues).includes('') || !isValidPasswordFormat}
              mode="primary"
              testID="btnSubmit"
              label={t('PreviousYear.efirma.CTA')}
            />
          </Styled.EfirmaContainer>
        </FormContainer>
      </Styled.ModalContent>
    </Styled.ModalContainer>
  );
};

export default EfirmaPreviousYearModal;
